
import { defineComponent, PropType } from "vue";
import { Campaign } from "@/models/campaign";
import { mapActions } from "pinia";
import { useAnalyticsStore } from "@/store/analytics.store";
import SurveyHeaderComponent from "@/views/survey/components/SurveyHeaderComponent.vue";
import FooterComponent from "@/components/recorder/partials/FooterComponent.vue";

interface VideoWithTypeInterface {
    url: string;
    type: string;
}

const allowedVideoExtensions = ['mp4', 'webm', 'ogg'];

interface VideoComponentDataInterface {
    isVideoPlaying: boolean;
    video: HTMLVideoElement | null;
    videoDuration: number;
    videoCurrentTime: number;
    didStartPlaying: boolean;
    videoLoaded: boolean;
}

export default defineComponent({
    components: {
        FooterComponent,
        SurveyHeaderComponent,
    },
    name: "SurveyVideoView",
    props: {
        campaign: {
            type: Object as PropType<Campaign>,
            required: true
        },
    },
    data(): VideoComponentDataInterface {
        return {
            isVideoPlaying: false,
            didStartPlaying: false,
            video: null,
            videoDuration: 0,
            videoCurrentTime: 0,
            videoLoaded: false,
        };
    },
    mounted(): void {
        this.trackEvent('creationVideoView', {
            ...this.$analytics.campaignParams(this.campaign),
        })

        this.video = this.$refs.video as HTMLVideoElement;
        this.video.addEventListener("loadedmetadata", this.onLoadedMetadata);
        this.video.addEventListener("timeupdate", this.onTimeUpdated);
        this.video.addEventListener("ended", this.onEnded);
    },
    beforeUnmount(): void {
        this.video?.pause();
        this.video?.removeEventListener("loadedmetadata", this.onLoadedMetadata);
        this.video?.removeEventListener("timeupdate", this.onTimeUpdated);
        this.video?.removeEventListener("ended", this.onEnded);
        this.video = null;
    },
    methods: {
        ...mapActions(useAnalyticsStore, ['trackEvent']),

        goToNextScreen(): void {
            this.trackEvent('videoPreRollSkipClicked', {
                ...this.$analytics.campaignParams(this.campaign),
            })
            this.$emit('finished');
        },

        onLoadedMetadata(): void {
            this.videoLoaded = true;
            this.videoDuration = this.video?.duration || 0;
        },

        onTimeUpdated(): void {
            this.videoCurrentTime = this.video?.currentTime || 0;
        },

        onEnded(): void {
            this.trackEvent('videoPreRollEnded', {
                ...this.$analytics.campaignParams(this.campaign),
            })
            this.$emit('finished');
        },

        async toggleVideoPlayback(): Promise<void> {
            if (this.isVideoPlaying) {
                this.pauseVideo();
                this.trackEvent('videoPreRollPauseClicked', {
                    ...this.$analytics.campaignParams(this.campaign),
                })
            } else {
                this.trackEvent('videoPreRollPlayClicked', {
                    ...this.$analytics.campaignParams(this.campaign),
                })
                await this.playVideo();
            }
        },

        async playVideo(): Promise<void> {
            this.isVideoPlaying = true;
            this.didStartPlaying = true;
            try {
                await this.video?.play();
            } catch (error) {
                console.log(error);
            }
        },

        pauseVideo(): void {
            this.isVideoPlaying = false;
            this.video?.pause();
        },
    },
    computed: {
        playbackDuration(): number {
            return (this.videoCurrentTime / this.videoDuration) * 100;
        },
        videos(): VideoWithTypeInterface[] {
            const videoURLs: VideoWithTypeInterface[] = [];

            this.campaign.videoURLs?.forEach((url) => {
                const extension = url.split('.').pop();
                const urlWithMediaParts = `${url}#t=0.001`;
                if (extension && allowedVideoExtensions.includes(extension)) {
                    switch (extension) {
                        case 'mp4':
                            videoURLs.push({ url: urlWithMediaParts, type: 'video/mp4' });
                            break;
                        case 'webm':
                            videoURLs.push({ url: urlWithMediaParts, type: 'video/webm' });
                            break;
                        case 'ogg':
                            videoURLs.push({ url: urlWithMediaParts, type: 'video/ogg' });
                            break;
                    }
                }
            });

            return videoURLs;
        },
    }
})
